/**
 * RunsHistogram
 *
 * Stacked bar chart showing run volume over the selected time window.
 * Each bar = one time bucket. Bar height = total runs. Red top = errored runs.
 *
 * Bucket sizing keeps bar count at ~85 regardless of range:
 *   24h → 20min buckets (72 bars)
 *   7d  → 2h  buckets  (84 bars)
 *   30d → 8h  buckets  (90 bars)
 *   90d → 1d  buckets  (90 bars)
 *  365d → 4d  buckets  (91 bars)
 *  Custom → nearest clean interval for ~85 bars
 *
 * Data is fetched from GET /agent-runs/histogram with the same filter params
 * as the main table. On error the chart is silently hidden.
 *
 * The bars follow the team's own volume, with no extra request: the busiest
 * bar in the period is full height, and a quiet range merges
 * neighbouring buckets so a few busy hours show as a few wide bars instead of
 * slivers in an empty strip.
 */
import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { dedupedGet } from 'shared/services/api/dedupedGet';

// ── Bucket helpers ────────────────────────────────────────────────────────────

const CLEAN_INTERVALS_SEC = [
  60,         // 1 min
  300,        // 5 min
  600,        // 10 min
  1200,       // 20 min
  1800,       // 30 min
  3600,       // 1 h
  7200,       // 2 h
  14400,      // 4 h
  28800,      // 8 h
  43200,      // 12 h
  86400,      // 1 d
  172800,     // 2 d
  259200,     // 3 d
  345600,     // 4 d
  604800,     // 1 w
];

const TARGET_BARS = 85;

function chooseBucketSec(fromIso, toIso) {
  const fromMs = fromIso ? new Date(fromIso).getTime() : Date.now() - 30 * 86400_000;
  const toMs   = toIso   ? new Date(toIso).getTime()   : Date.now();
  const rangeSec = Math.max((toMs - fromMs) / 1000, 3600);
  const rawSec   = rangeSec / TARGET_BARS;
  return CLEAN_INTERVALS_SEC.find(i => i >= rawSec) ?? CLEAN_INTERVALS_SEC.at(-1);
}

/** Short axis label.
 *  Rule: if the total range spans more than 24h, show date only (e.g. "May 21")
 *  — even if buckets are sub-day. Below 24h we show time-of-day.
 */
function formatAxisLabel(isoStart, rangeMs) {
  const d = new Date(isoStart);
  if (rangeMs <= 24 * 3600_000) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Full label for the tooltip — always includes date + time. */
function formatFullLabel(isoStart) {
  const d = new Date(isoStart);
  return d.toLocaleString([], {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Build a full bucket grid spanning the selected range.
 *
 * IMPORTANT: TimescaleDB's `time_bucket(interval, ts)` anchors to
 * 2000-01-03 00:00:00 UTC by default, NOT to the Unix epoch. For sub-day
 * buckets (≤ 1d) the two anchors happen to align (the offset is a whole
 * number of any sub-day interval), but for 2d / 3d / 4d / 1w buckets they
 * do NOT align — and those are exactly the bucket sizes chosen for the
 * 3M / 6M / 12M / custom ranges. If we snap to epoch 0 here, every grid
 * key misses the server's bucket_start values and the chart renders all
 * zeros ("histogram doesn't load on 3m/12m/custom"). Snap to the same
 * origin Timescale uses so keys line up exactly.
 *
 * Returns: [{ bucket_start: ISO, total, errors, ok }, ...] with zeros for
 * empty intervals.
 */
const TS_BUCKET_ORIGIN_MS = Date.UTC(2000, 0, 3); // 2000-01-03 00:00:00 UTC

function denseBuckets(apiBuckets, timeRange, bucketSec) {
  const fromMs = timeRange?.from
    ? new Date(timeRange.from).getTime()
    : (apiBuckets[0] ? new Date(apiBuckets[0].bucket_start).getTime() : Date.now() - 30 * 86400_000);
  const toMs = timeRange?.to
    ? new Date(timeRange.to).getTime()
    : Date.now();

  const stepMs = bucketSec * 1000;
  // Snap to Timescale's time_bucket origin so client keys match server keys
  // for every bucket size (including multi-day and weekly buckets).
  const startMs = Math.floor((fromMs - TS_BUCKET_ORIGIN_MS) / stepMs) * stepMs + TS_BUCKET_ORIGIN_MS;
  const endMs   = Math.ceil((toMs   - TS_BUCKET_ORIGIN_MS) / stepMs) * stepMs + TS_BUCKET_ORIGIN_MS;

  // Index API buckets by their epoch ms for O(1) merge.
  const byStart = new Map();
  for (const b of apiBuckets) {
    byStart.set(new Date(b.bucket_start).getTime(), b);
  }

  const out = [];
  for (let t = startMs; t < endMs; t += stepMs) {
    const hit = byStart.get(t);
    out.push(
      hit
        ? { bucket_start: new Date(t).toISOString(), total: hit.total, errors: hit.errors, ok: hit.ok }
        : { bucket_start: new Date(t).toISOString(), total: 0, errors: 0, ok: 0 }
    );
  }
  return out;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function RunsHistogram({
  teamId,
  timeRange,
  modularFilters,
  onTotals,
  endpoint = '/agent-runs/histogram',
  /** When false, bars render as a single grey block (no red error stacking). */
  showErrors = true,
  /** Optional extra query params to forward to the endpoint (e.g. websiteId). */
  extraParams = null,
  /** What one unit is called in the summary ("runs", "spans", "events"). */
  noun = 'runs',
}) {
  const [bars, setBars]       = useState(null); // null = loading first time, [] = empty/error
  const [bucketSec, setBucketSec] = useState(86400);
  const [tooltip, setTooltip] = useState(null);
  const reqId = useRef(0);

  // Stable signature for the fetch params — only changes when something we
  // actually send to the API changes. Prevents re-fires on unrelated parent
  // renders that hand us a fresh object/array reference.
  const fetchKey = `${endpoint}|${teamId || ''}|${timeRange?.from || ''}|${timeRange?.to || ''}|${
    modularFilters?.length ? JSON.stringify(modularFilters) : ''
  }|${extraParams ? JSON.stringify(extraParams) : ''}`;

  // Hold the latest onTotals in a ref so we don't need it as an effect dep
  // (parent passes a state setter — referentially stable in practice — but we
  // double-belt this so any future inline lambda doesn't trigger refetches).
  const onTotalsRef = useRef(onTotals);
  useEffect(() => { onTotalsRef.current = onTotals; }, [onTotals]);

  useEffect(() => {
    if (!teamId) return undefined;
    const id = ++reqId.current;
    let cancelled = false;

    const bSec = chooseBucketSec(timeRange?.from, timeRange?.to);
    setBucketSec(bSec);

    // Send both naming conventions so the same component works against every
    // endpoint without a per-endpoint param map:
    //   agent-runs / agent-spans / events: team_id, time_start, time_end
    //   sessions:                          teamId, from, to
    const params = { team_id: teamId, teamId, bucket_seconds: bSec };
    if (timeRange?.from) { params.time_start = timeRange.from; params.from = timeRange.from; }
    if (timeRange?.to)   { params.time_end   = timeRange.to;   params.to   = timeRange.to;   }
    if (modularFilters?.length)
      params.modularFilters = encodeURIComponent(JSON.stringify(modularFilters));
    if (extraParams && typeof extraParams === 'object') {
      for (const [k, v] of Object.entries(extraParams)) {
        if (v != null && v !== '') params[k] = v;
      }
    }

    // NOTE: we intentionally do NOT clear `bars` here — keep the old chart
    // visible during the refetch so the view doesn't flash empty.
    dedupedGet(endpoint, { params }).then(
      ({ data }) => {
        if (cancelled || id !== reqId.current) return;
        const dense = denseBuckets(data?.buckets ?? [], timeRange, bSec);
        setBars(dense);

        const total  = dense.reduce((sum, b) => sum + (b.total  || 0), 0);
        const errors = dense.reduce((sum, b) => sum + (b.errors || 0), 0);
        onTotalsRef.current?.({ total, errors });
      },
      () => {
        if (cancelled || id !== reqId.current) return;
        setBars([]);
        onTotalsRef.current?.({ total: 0, errors: 0 });
      }
    );

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  // Don't render if no data or empty
  if (!bars || bars.length === 0) return null;

  // X: fewer, wider bars when little is happening (merging neighbours, never splitting).
  const nonZero = bars.filter((b) => b.total > 0).length;
  const targetBars = Math.min(bars.length, Math.max(24, nonZero * 4));
  const factor = Math.max(1, Math.ceil(bars.length / targetBars));
  const shown = factor === 1 ? bars : mergeBuckets(bars, factor);
  const stepSec = bucketSec * factor;
  const rangeMs = shown.length > 1
    ? new Date(shown[shown.length - 1].bucket_start).getTime() - new Date(shown[0].bucket_start).getTime() + stepSec * 1000
    : stepSec * 1000;
  // Y: the busiest bar in this period fills the chart.
  const maxTotal = Math.max(...shown.map((b) => b.total || 0), 0);
  const top = Math.max(maxTotal, 1);
  const chartH = 84;

  // Show a label every ~Nth bar so they don't overlap
  const labelEvery = Math.max(1, Math.ceil(shown.length / 8));

  return (
    <div
      className="relative select-none pb-2 pt-3"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {maxTotal === 0 ? (
        <div className="flex items-center justify-center text-[12px] text-[color:var(--text-muted)]" style={{ height: 64 }}>
          No {noun} in this period.
        </div>
      ) : (
      <>
      <div className="relative flex items-end gap-[2px] border-b border-[color:var(--border-default)]" style={{ height: chartH }}>
        {shown.map((bar, i) => {
          const totalH = bar.total > 0 ? Math.max((bar.total / top) * 100, 3) : 0;
          // Volume-only: one grey segment, no error stacking.
          const errorH = showErrors && bar.total > 0
            ? Math.max((bar.errors / Math.max(bar.total, 1)) * totalH, bar.errors > 0 ? 3 : 0)
            : 0;
          const okH = Math.max(totalH - errorH, 0);

          return (
            <div
              key={i}
              className="group/bar relative flex flex-1 flex-col-reverse cursor-default"
              style={{ height: '100%' }}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({
                  centerX: rect.left + rect.width / 2,
                  barTop: rect.top,
                  bar,
                  i,
                });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* OK segment (bottom) */}
              {okH > 0 && (
                <div
                  className="opacity-50 transition-opacity group-hover/bar:opacity-80"
                  style={{
                    height: `${okH}%`,
                    backgroundColor: 'var(--text-muted)',
                    minHeight: 2,
                    borderRadius: errorH > 0 ? 0 : '2px 2px 0 0',
                  }}
                />
              )}
              {/* Error segment (top, red) */}
              {errorH > 0 && (
                <div
                  style={{
                    height: `${errorH}%`,
                    backgroundColor: 'var(--feedback-error)',
                    opacity: 0.85,
                    minHeight: 2,
                    borderRadius: '2px 2px 0 0',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div className="relative mt-1" style={{ height: 14 }}>
        {shown.map((bar, i) => {
          if (i % labelEvery !== 0) return null;
          const isFirst = i === 0;
          // Last index that gets a label (multiple of labelEvery ≤ shown.length-1)
          const lastLabeledIdx = Math.floor((shown.length - 1) / labelEvery) * labelEvery;
          const isLast = i === lastLabeledIdx;
          // Center of this bar as a % of the chart width
          const centerPct = ((i + 0.5) / shown.length) * 100;

          // Edge clamping: first label flush-left, last flush-right, rest centered.
          const positionStyle = isFirst
            ? { left: 0 }
            : isLast
              ? { right: 0 }
              : { left: `${centerPct}%`, transform: 'translateX(-50%)' };

          return (
            <span
              key={i}
              className="absolute bottom-0 whitespace-nowrap text-[10px] text-[color:var(--text-muted)]"
              style={positionStyle}
            >
              {formatAxisLabel(bar.bucket_start, rangeMs)}
            </span>
          );
        })}
      </div>

      </>
      )}

      {/* Hover tooltip: fixed-positioned so it escapes any clipped ancestor */}
      {tooltip && (
        <div
          className="pointer-events-none fixed z-[100] rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 shadow-lg"
          style={{
            top: Math.max(tooltip.barTop - 8, 8),
            left: Math.min(
              Math.max(tooltip.centerX, 80),
              (typeof window !== 'undefined' ? window.innerWidth : 1024) - 160
            ),
            transform: 'translate(-50%, -100%)',
            minWidth: 140,
          }}
        >
          <div className="mb-1.5 text-[11px] font-medium text-[color:var(--text-secondary)]">
            {formatFullLabel(tooltip.bar.bucket_start)}
          </div>
          <div className="flex items-center justify-between gap-4 text-[11px]">
            <span className="text-[color:var(--text-secondary)]">Total</span>
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{(tooltip.bar.total ?? 0).toLocaleString()}</span>
          </div>
          {showErrors && (tooltip.bar.errors ?? 0) > 0 && (
            <div className="flex items-center justify-between gap-4 text-[11px]">
              <span className="text-[color:var(--feedback-error)]">Error</span>
              <span className="font-semibold tabular-nums text-[color:var(--feedback-error)]">{tooltip.bar.errors.toLocaleString()}</span>
            </div>
          )}
          {showErrors && (tooltip.bar.ok ?? 0) > 0 && (
            <div className="flex items-center justify-between gap-4 text-[11px]">
              <span className="text-[color:var(--text-muted)]">OK</span>
              <span className="font-semibold text-[color:var(--text-primary)]">{tooltip.bar.ok ?? (tooltip.bar.total - (tooltip.bar.errors ?? 0))}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Scale helpers ───────────────────────────────────────────────────────── */

/** Merge every `factor` neighbouring buckets into one. Exported for tests. */
export function mergeBuckets(bars, factor) {
  const out = [];
  for (let i = 0; i < bars.length; i += factor) {
    const group = bars.slice(i, i + factor);
    out.push({
      bucket_start: group[0].bucket_start,
      total: group.reduce((a, b) => a + (b.total || 0), 0),
      errors: group.reduce((a, b) => a + (b.errors || 0), 0),
      ok: group.reduce((a, b) => a + (b.ok || 0), 0),
    });
  }
  return out;
}

